import {coordsToKey} from "../coords/coordsToKey";
import {HEX_DIRECTIONS} from "../../constants/geometry";
import {SPECIAL_BUILDING_TOKENS, TOKENS_RELIED_ON_QUARTER} from "../../constants/tokens";
import {checkIfSpecialBuilding} from "../buildings/checkIfSpecialBuilding";
import {checkIfAnyQuarter} from "../buildings/checkIfAnyQuarter";
import {FORTRESS_MAX_DAMAGE, SPECIAL_BUILDING_MAX_DAMAGE} from "../../configs/specialBuildings";
import {checkIfKeyPoint} from "../buildings/checkIfKeyPoint";

/**
 * Функция проверки, заблокировано ли строение/ключевая точка вражеским отрядом.
 * По правилам игры отряд противника блокирует работу строений и ключевых точек
 * на смежных ячейках, и на той, на которой он сам расположен.
 *
 * Для фермы, казармы, рудника, великого рудника, руин, великих руин:
 * - если сама ячейка заблокирована противником - возвращаем true
 * - если эта ячейка из перечня указанных выше строений, то ищем смежные кварталы.
 *   Проверяем, есть ли хотя бы один из этих кварталов не заблокирован, возвращаем false.
 *   Иначе возвращаем true
 *
 * @param hexKey
 * @param tokenType
 * @param currentPlayerId
 * @param units
 * @param hexes - Объект ячеек поля.
 * Если не передан, то не будет выполнена проверка для TOKENS_RELIED_ON_QUARTER
 * (например, это не нужно делать для кварталов и спецстроений, или при расчете доступных ячеек)
 * @param specialBuildings
 * @param buildingsDamage
 * @returns {boolean}
 */
export const checkIfHexBlockedByEnemyUnit = (hexKey, tokenType, currentPlayerId, units, hexes, specialBuildings, buildingsDamage) => {
  if (tokenType === SPECIAL_BUILDING_TOKENS.FORTRESS) {
    return false;
  }

  // Отряд, нанятый крысолюдами ЗА СНАБЖЕНИЕ (isSupplyRecruit), появится на поле
  // только по итогу фазы снабжения — до этого он никому ничего не блокирует. В
  // поочередной фазе такой отряд бывает лишь у самого ходящего (метку снимает
  // применение его снабжения), а свой отряд и так не блокирует — там это ничего
  // не меняет; в одновременной фазе итог соседа не зависит от того, кто успел
  // нанять раньше
  const enemyUnits = units.filter(u => u.ownerId !== currentPlayerId && !u.isSupplyRecruit);

  // Координаты проверяемой ячейки
  const [hexQ, hexR] = hexKey.split(',').map(Number);

  // Вражеский отряд на самой ячейке или на смежной — строение/ключевая точка
  // заблокирована
  if (checkIfEnemyUnitOnOrAdjacent(hexKey, hexQ, hexR, enemyUnits)) {
    return true;
  }

  // Не выполняем дальнейшие проверки, когда не передан токен
  // (например, если проверяется доступность проведения маршрута снабжения или доступность ячейки для строительства обычного строения).
  if (!tokenType) {
    return false;
  }

  // Проверяем наличие повреждений у строения. Если есть хотя бы 1 повреждение - строение заблокировано.
  const isSpecialBuilding = checkIfSpecialBuilding(tokenType)
  const isKeyPoint = checkIfKeyPoint(tokenType);
  if (!isKeyPoint) {
    const damage = buildingsDamage[hexKey] || 0;
    if (damage > 0) {
      if (isSpecialBuilding) {
        return true;
      } else {
        if (checkIfCommonBuildingBlockedByDamage(hexKey, specialBuildings, buildingsDamage)) {
          return true
        }
      }
    }
  }

  // Специальные строения и строения, не полагающиеся на смежные кварталы,
  // могут быть заблокированы только смежным отрядом противника или повреждениями, что было проверено выше.
  if (isSpecialBuilding || !TOKENS_RELIED_ON_QUARTER.includes(tokenType)) {
    return false;
  }

  // Для строений, которые полагаются на смежный квартал, ищем незаблокированный смежный квартал.
  let hasUnblockedQuarter = false;

  for (const [dq, dr] of HEX_DIRECTIONS) {
    const adjQ = hexQ + dq;
    const adjR = hexR + dr;
    const adjKey = coordsToKey(adjQ, adjR);
    const token = hexes[adjKey];

    // Проверяем, является ли смежная ячейка кварталом
    if (token && checkIfAnyQuarter(token)) {
      // Проверяем, заблокирован ли этот квартал вражеским отрядом (на нем или рядом)
      const isQuarterBlocked = checkIfEnemyUnitOnOrAdjacent(adjKey, adjQ, adjR, enemyUnits);

      // Если квартал не заблокирован отрядами противников:
      if (!isQuarterBlocked) {
        // Проверяем, что квартал не заблокирован повреждениями
        if (!checkIfCommonBuildingBlockedByDamage(adjKey, specialBuildings, buildingsDamage)) {
          hasUnblockedQuarter = true;
          break;
        }
      }
    }
  }

  // Если есть хотя бы один не заблокированный квартал, строение работает
  // Все смежные кварталы заблокированы или их нет - строение заблокировано
  return !hasUnblockedQuarter;
};

/**
 * Стоит ли один из вражеских отрядов на ячейке hexKey или на одной из смежных.
 * Функция горячая — ее зовут поиски путей на каждом шаге, — поэтому ключи
 * смежных ячеек строятся один раз, а не заново на каждый отряд.
 *
 * Сравнение то же, что было: сама ячейка — по ее ключу как есть, смежные — по
 * ключам, собранным из координат (coordsToKey)
 */
const checkIfEnemyUnitOnOrAdjacent = (hexKey, hexQ, hexR, enemyUnits) => {
  const adjacentKeys = HEX_DIRECTIONS.map(([dq, dr]) => coordsToKey(hexQ + dq, hexR + dr));

  for (const enemyUnit of enemyUnits) {
    if (enemyUnit.hexKey === hexKey || adjacentKeys.includes(enemyUnit.hexKey)) {
      return true;
    }
  }
  return false;
};

const checkIfCommonBuildingBlockedByDamage = (hexKey, specialBuildings, buildingsDamage) => {
  const damage = buildingsDamage[hexKey] || 0;
  if (damage <= 0) {
    return false;
  }

  const specialBuildingOnHex = specialBuildings[hexKey];
  if (specialBuildingOnHex) {
    if (specialBuildingOnHex === SPECIAL_BUILDING_TOKENS.FORTRESS) {
      return damage > FORTRESS_MAX_DAMAGE;
    } else {
      return damage > SPECIAL_BUILDING_MAX_DAMAGE;
    }
  } else {
    return true;
  }
}